import { Link, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang, LANGS } from "@/context/LanguageContext";
import { Menu, X, LogOut, LayoutDashboard, Compass, User, Globe, ChevronDown, Loader2 } from "lucide-react";

function LanguageSwitcher({ block = false }) {
  const { lang, setLang, translating } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const current = LANGS.find((l) => l.code === lang) || LANGS[0];
  return (
    <div className={`relative ${block ? "w-full" : ""}`} ref={ref} data-no-translate translate="no" data-testid="lang-switcher">
      <button onClick={() => setOpen(!open)} data-testid="lang-toggle"
        className={`flex items-center gap-1.5 rounded-full border border-primary/20 px-3 py-2 text-sm font-medium text-foreground/80 hover:bg-secondary transition-colors ${block ? "w-full justify-between" : ""}`}>
        <span className="flex items-center gap-1.5">{translating ? <Loader2 size={16} className="animate-spin text-accent" /> : <Globe size={16} />} {current.label}</span>
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className={`absolute ${block ? "left-0 right-0" : "right-0"} mt-2 max-h-72 overflow-y-auto rounded-xl border border-primary/10 bg-card shadow-xl p-1.5 z-50 min-w-[150px]`} data-testid="lang-menu">
          {LANGS.map((l) => (
            <button key={l.code} onClick={() => { setLang(l.code); setOpen(false); }} data-testid={`lang-${l.code}`}
              className={`w-full text-left rounded-lg px-3 py-2 text-sm transition-colors ${lang === l.code ? "bg-accent text-accent-foreground" : "text-foreground hover:bg-secondary"}`}>
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const NAV = [
  { to: "/explore", label: "Explore" },
  { to: "/spiritual", label: "Spiritual" },
  { to: "/adventure", label: "Adventure" },
  { to: "/planner", label: "AI Planner" },
  { to: "/flights", label: "Flights" },
  { to: "/hotels", label: "Hotels" },
  { to: "/buses", label: "Buses" },
  { to: "/cabs", label: "Cabs" },
  { to: "/about", label: "About" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="glass-nav sticky top-0 z-40 border-b border-primary/10">
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <span className="grid place-items-center w-9 h-9 rounded-full bg-primary text-primary-foreground font-display text-lg leading-none">z</span>
          <span className="font-display text-2xl tracking-tight text-foreground">zatayoo</span>
        </Link>

        <div className="hidden lg:flex items-center gap-7">
          {NAV.map((l) => (
            <Link key={l.to} to={l.to} className="text-sm font-medium text-foreground/80 hover:text-accent transition-colors" data-testid={`nav-${l.label.toLowerCase()}`}>
              {l.label}
            </Link>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher />
          {user ? (
            <>
              {user.role === "admin" && (
                <button onClick={() => navigate("/admin")} className="text-sm font-medium text-foreground/80 hover:text-accent flex items-center gap-1.5" data-testid="nav-admin"><LayoutDashboard size={16} /> Admin</button>
              )}
              <button onClick={() => navigate("/my-bookings")} className="text-sm font-medium text-foreground/80 hover:text-accent flex items-center gap-1.5" data-testid="nav-my-bookings"><User size={16} /> Trips</button>
              <button onClick={() => { logout(); navigate("/"); }} className="text-sm font-medium text-foreground/60 hover:text-destructive flex items-center gap-1.5" data-testid="nav-logout"><LogOut size={16} /></button>
            </>
          ) : (
            <Link to="/login" className="text-sm font-medium text-foreground/80 hover:text-accent" data-testid="nav-login">Sign in</Link>
          )}
          <Link to="/explore" className="rounded-full bg-accent text-accent-foreground px-5 py-2.5 text-sm font-semibold hover:-translate-y-px transition-transform flex items-center gap-1.5" data-testid="nav-explore-cta"><Compass size={15} /> Explore</Link>
        </div>

        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle">{open ? <X /> : <Menu />}</button>
      </nav>

      {open && (
        <div className="lg:hidden border-t border-primary/10 px-5 py-4 flex flex-col gap-3 bg-background" data-testid="nav-mobile-menu">
          {NAV.map((l) => <Link key={l.to} to={l.to} className="text-sm font-medium" onClick={() => setOpen(false)}>{l.label}</Link>)}
          <div className="pt-2"><LanguageSwitcher block /></div>
          {user ? (
            <>
              {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="text-sm font-medium">Admin</Link>}
              <Link to="/my-bookings" onClick={() => setOpen(false)} className="text-sm font-medium">My trips</Link>
              <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="text-left text-sm font-medium text-destructive">Logout</button>
            </>
          ) : <Link to="/login" onClick={() => setOpen(false)} className="text-sm font-medium">Sign in</Link>}
        </div>
      )}
    </header>
  );
}
